import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { jbApi } from '../api';
import '../job-detail-v2.css';

const LOGO_COLORS = ['#1B6B72','#0D2137','#C9A84C','#2563eb','#7c3aed','#059669'];
function logoColor(text = '') {
  const code = [...text].reduce((s, c) => s + c.charCodeAt(0), 0);
  return LOGO_COLORS[code % LOGO_COLORS.length];
}

function fmtLabel(str = '') {
  return str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatSalary(raw = '') {
  const nums = raw.match(/[\d,.]+k?/gi);
  if (!nums || nums.length === 0) return raw;
  const parse = s => {
    const n = s.replace(/,/g, '').toLowerCase();
    const val = parseFloat(n) * (n.endsWith('k') ? 1000 : 1);
    return isNaN(val) ? null : val;
  };
  const fmt = n => '$' + Math.round(n).toLocaleString('en-US');
  if (nums.length === 1) {
    const v = parse(nums[0]);
    return v ? fmt(v) : raw;
  }
  const a = parse(nums[0]);
  const b = parse(nums[1]);
  if (!a || !b) return raw;
  return `${fmt(a)} \u2014 ${fmt(b)}`;
}

export default function JobDetailPage() {
  const { job_id } = useParams();
  const [job, setJob]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName]     = useState('');
  const [email, setEmail]   = useState('');
  const [optIn, setOptIn]   = useState(false);

  useEffect(() => {
    if (!job_id) return;
    setLoading(true);
    jbApi.getJob(job_id)
      .then(setJob)
      .catch(() => { window.location.href = '/career-hub'; })
      .finally(() => setLoading(false));
  }, [job_id]);

  function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    if (job?.apply_url) {
      window.location.href = job.apply_url;
    }
  }

  if (loading) {
    return (
      <div style={{ background: '#f8f7f4', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#64748b', fontFamily: 'Inter, sans-serif', fontSize: 14 }}>Loading…</div>
      </div>
    );
  }

  if (!job) return null;

  const salary  = job.salary_range ? formatSalary(job.salary_range) : null;
  const initial = job.logo_initial || (job.organization || '??').slice(0, 2).toUpperCase();
  const bg      = logoColor(job.organization || '');

  return (
    <>
      {/* ── HERO ── */}
      <div className="job-hero" data-testid="job-detail-page">
        <div className="job-hero-inner">
          <div className="job-logo" style={{ background: job.employer_logo_url ? '#fff' : bg }}>
            {job.employer_logo_url
              ? <img src={job.employer_logo_url} alt={job.organization} />
              : initial}
          </div>
          <div className="job-hero-content">
            <div className="job-org">{job.organization}</div>
            <h1 className="job-title" data-testid="job-title">{job.title}</h1>
            <div className="job-badges">
              <span className="job-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                </svg>
                {job.location}
              </span>
              <span className="job-badge">{fmtLabel(job.work_type)}</span>
              <span className="job-badge">{fmtLabel(job.experience_level)} Level</span>
              {salary && <span className="job-badge">{salary}</span>}
              {job.category && <span className="job-badge">{job.category}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* ── BREADCRUMB ── */}
      <div className="breadcrumb">
        <Link to="/career-hub">← Back to Career Hub</Link>
        <span className="breadcrumb-sep">/</span>
        <span>{job.category}</span>
        <span className="breadcrumb-sep">/</span>
        <span>{job.title}</span>
      </div>

      {/* ── BODY ── */}
      <div className="job-body">

        {/* LEFT — CONTENT */}
        <div className="job-content">
          {job.employer_id === 'usajobs' && (
            <div className="source-badge">
              <span className="source-dot" />
              Federal Opportunity · USAJobs
            </div>
          )}

          <div className="job-section">
            <div className="job-section-title">About the Role</div>
            <div
              className="job-description"
              dangerouslySetInnerHTML={{ __html: job.description }}
            />
          </div>
        </div>

        {/* RIGHT — SIDEBAR */}
        <div className="job-sidebar">

          {/* APPLY CARD */}
          <form className="apply-card" onSubmit={handleSubmit} data-testid="apply-form">
            <div className="apply-card-header">
              <div className="apply-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13"/>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              </div>
              <div className="apply-card-header-text">
                <div className="apply-card-title">Apply Now</div>
                <div className="apply-card-subtitle">Submit your info to continue to the application</div>
              </div>
            </div>
            <div className="apply-card-body">
              <div className="form-group">
                <label className="form-label">Name <span className="required">*</span></label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="First and Last Name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  data-testid="apply-name-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email <span className="required">*</span></label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  data-testid="apply-email-input"
                />
              </div>
              <div className="form-checkbox-row">
                <input
                  className="form-checkbox"
                  type="checkbox"
                  id="opt-in"
                  checked={optIn}
                  onChange={e => setOptIn(e.target.checked)}
                  data-testid="apply-optin-checkbox"
                />
                <label className="form-checkbox-label" htmlFor="opt-in">
                  Yes, I'd like to receive job alerts and career tips from Opportunity Post
                </label>
              </div>
              <button className="btn-apply" type="submit" data-testid="apply-submit-btn">
                Continue to Apply
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              </button>
              <div className="apply-disclaimer">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Your information is secure and shared only with the hiring partner
              </div>
            </div>
          </form>

          {/* JOB DETAILS CARD */}
          <div className="details-card">
            <div className="details-card-title">Job Details</div>
            {job.category && (
              <div className="detail-row">
                <span className="detail-label">Category</span>
                <span className="detail-value">{job.category}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Work Type</span>
              <span className="detail-value">{fmtLabel(job.work_type)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Experience</span>
              <span className="detail-value">{fmtLabel(job.experience_level)}</span>
            </div>
            {salary && (
              <div className="detail-row">
                <span className="detail-label">Salary</span>
                <span className="detail-value">{salary}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Location</span>
              <span className="detail-value">{job.location}</span>
            </div>
            {job.source_name && (
              <div className="detail-row">
                <span className="detail-label">Source</span>
                <span className="detail-value">{job.source_name}</span>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── FOOTER DISCLAIMER ── */}
      <div className="job-footer-disclaimer">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        By submitting your information you will be redirected to the hiring partner's application site to complete your application. Opportunity Post is not the employer and does not make hiring decisions.
      </div>
    </>
  );
}
